/**
 * Embed containment policy — what the marketplace may do while embedded.
 *
 * Three rules, co-located: which marketplace interruptions (promos, NPS, app
 * install, last-minute) may surface, whether profile onboarding may surface,
 * and which routes a visitor may reach. All read the runtime context from
 * {@link getEmbedContext}; App consumes the first two and the shared
 * containment policy consumes the route rule, instead of spreading `!isEmbed`
 * checks through their predicates.
 */

import { APP_ROUTES } from '@/constants/pages';
import { getEmbedContext } from '@/helpers/embed';
import { matchPath } from 'react-router-dom';

/**
 * Route patterns reachable inside the widget. Anything outside this list — SERP,
 * marketplace landing, FAQ, account, etc. — is blocked so the visitor stays in
 * the merchant-specific booking flow.
 *
 * The booking-flow step pages, the confirmation and the pay page need NO entry
 * here: they run under /embed in the widget's own router
 * (features/booking-widget/App.tsx) and never pass through AppRoutes. This list
 * guards the marketplace app when it renders framed — the checkout and
 * payment-validation routes.
 *
 * The legacy Adyen return routes (3DS, Swish, the cof/Swish prepay validators)
 * redirect to a confirmation that is NOT listed. They stay because the widget
 * offers Bokadirekt Pay only, so they are believed unreachable framed — see
 * docs/decisions/2026-09-02-widget-confirmation-in-frame.md before touching them.
 */
export const EMBED_ALLOWED_ROUTES: string[] = [
  APP_ROUTES.BOOKING_CALENDAR,
  APP_ROUTES.BOOKING_CHECKOUT,
  APP_ROUTES.BOOKING_CHECKOUT_KLARNA,
  APP_ROUTES.BOOKING_CHECKOUT_QLIRO,
  APP_ROUTES.BOOKING_CHECKOUT_VALIDATE_3DS,
  APP_ROUTES.BOOKING_CHECKOUT_VALIDATE_SWISH,
  APP_ROUTES.VALIDATE_BOKADIREKT_PAY,
  APP_ROUTES.VALIDATE_SWISH_PAYMENT,
  APP_ROUTES.VALIDATE_COF_PAYMENT,
  APP_ROUTES.VALIDATE_QLIRO_PAYMENT,
];

/**
 * Whether marketplace interruptions may surface. Suppressed in every embedded
 * context (the framed SPA and the pay popup), allowed only standalone.
 */
export function interruptionsAllowed(): boolean {
  return getEmbedContext() === 'standalone';
}

/**
 * Whether the global profile-onboarding modal may surface. Never in either
 * popup: profile completion belongs to the widget checkout, and the modal would
 * sit on top of the consent screen — or, in the pay window, on top of the Adyen
 * payment form — for a nameless site user.
 */
export function profileOnboardingAllowed(): boolean {
  const context = getEmbedContext();
  return context !== 'loginPopup' && context !== 'paymentPopup';
}

/** Whether a path is reachable inside the widget. */
export function allowsRoute(path: string): boolean {
  return EMBED_ALLOWED_ROUTES.some((pattern) => matchPath(path, { path: pattern, exact: true }) != null);
}
